import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useState } from 'react';

import { useCreatePieceDocumentMutation, useUpdatePieceDocumentMutation } from '@api/piece';
import { DialogMailpieceOpenAndScan } from '@components/DialogMailpieceOpenAndScan';
import { FileUploadType, Upload } from '@components/Upload';

import { getUploadPieceDocUrl } from '@src/api/piece';

import { ActionComponentProps } from '../../../../index';

const StyledUpload = styled('div')(() => ({
  '& img': {
    objectFit: 'contain',
  },
}));

type DialogOpenAndScanProps = ActionComponentProps & {
  onOpenAndScanComplete: () => void;
};

export const DialogOpenAndScan = ({
  mailpieces,
  actionStarted,
  onActionFinish,
  onOpenAndScanComplete,
}: DialogOpenAndScanProps) => {
  const [createPieceDocument] = useCreatePieceDocumentMutation();
  const [updatePieceDocument] = useUpdatePieceDocumentMutation();

  const mailpiece = mailpieces[0];

  const [openAndScanDialogOpen, setOpenAndScanDialogOpen] = useState<boolean>(false);
  const [file, setFile] = useState<FileUploadType | null>(null);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState<'scan' | 'upload'>('scan');

  const handleDrop = async (files: File[]) => {
    if (files.length !== 1) return;

    const nextFile: FileUploadType = files[0];

    setFile(nextFile);
  };

  const handlePieceDocumentReady = () => {
    setFile(null);
    setOpenAndScanDialogOpen(false);
    setMode('scan');
    onOpenAndScanComplete();
  };

  const handleConfirmUpload = async () => {
    if (!file) return;

    try {
      setLoading(true);
      const uploadUrl = await getUploadPieceDocUrl(mailpiece.id);
      const headers = { 'Content-Type': 'application/pdf' };
      const arrayBuffer = await (file as File).arrayBuffer();
      await fetch(uploadUrl.pdfUrl, { body: new Blob([new Uint8Array(arrayBuffer)]), headers, method: 'PUT' });

      let pieceDoc = mailpiece?.pieceDoc;
      if (!pieceDoc) {
        pieceDoc = await createPieceDocument({ pieceId: mailpiece.id, skipInvalidation: true }).unwrap();
      }

      await updatePieceDocument({ id: pieceDoc.id, pdfKey: uploadUrl.pdfKey, pieceId: mailpiece.id }).unwrap();

      handlePieceDocumentReady();
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmScan = () => {
    setOpenAndScanDialogOpen(true);
  };

  const handleClose = () => {
    setFile(null);
    setOpenAndScanDialogOpen(false);
    setMode('scan');
    onActionFinish();
  };

  return (
    <>
      <Dialog maxWidth="sm" open={actionStarted && !openAndScanDialogOpen} onClose={handleClose} fullWidth>
        <DialogTitle sx={{ p: 3 }}>Open & Scan</DialogTitle>
        <DialogContent>
          <Stack direction="column" spacing={2}>
            <Typography variant="body1" color="text.secondary">
              Choose how you would like to add the contents of this item.
            </Typography>
            <RadioGroup value={mode}>
              <FormControlLabel
                label="Scan with this device"
                control={<Radio value="scan" onChange={() => setMode('scan')} />}
              />
              <FormControlLabel
                label="Upload PDF file"
                control={<Radio value="upload" onChange={() => setMode('upload')} />}
              />
            </RadioGroup>

            {mode === 'upload' && (
              <StyledUpload>
                <Upload
                  value={file}
                  onDrop={handleDrop}
                  accept={{
                    '.pdf': [],
                  }}
                />
              </StyledUpload>
            )}
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button variant="text" color="inherit" onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={mode === 'scan' ? handleConfirmScan : handleConfirmUpload}
            disabled={loading || (!file && mode === 'upload')}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>

      {actionStarted && openAndScanDialogOpen && (
        <DialogMailpieceOpenAndScan
          currentPiece={mailpiece}
          onFinish={handlePieceDocumentReady}
          onClose={handleClose}
        />
      )}
    </>
  );
};
